import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'X-Tenant-Slug': 'demo-brokerage', // Default tenant for development
  },
  timeout: 10000,
});

// Token management
const TOKEN_KEY = 'signals_access_token';
const REFRESH_TOKEN_KEY = 'signals_refresh_token';

export const getAccessToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY);
};

export const setAccessToken = (token: string): void => {
  localStorage.setItem(TOKEN_KEY, token);
};

export const getRefreshToken = (): string | null => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

export const setRefreshToken = (token: string): void => {
  localStorage.setItem(REFRESH_TOKEN_KEY, token);
};

export const clearTokens = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

// Request interceptor - Add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Unwrap nested data and handle 401 errors
api.interceptors.response.use(
  (response) => {
    // API responses are wrapped in { success: true, data: {...} }
    // For paginated responses, preserve pagination info
    // Paginated responses have: { success, data: [], pagination: {} }
    if (response.data && typeof response.data === 'object' && 'success' in response.data) {
      if ('pagination' in response.data) {
        // Paginated response - restructure to { data: [], meta: {} }
        response.data = {
          data: response.data.data,
          meta: {
            page: response.data.pagination.page,
            limit: response.data.pagination.limit,
            total: response.data.pagination.total,
            totalPages: response.data.pagination.totalPages,
          },
        };
      } else if ('data' in response.data) {
        // Non-paginated response - unwrap the nested data
        response.data = response.data.data;
      }
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Skip 401 handling for auth endpoints (login, register, forgot-password)
    // These endpoints return 401 for invalid credentials, which is expected
    const isAuthEndpoint = originalRequest.url?.includes('/auth/');

    // If 401 and not a retry and not an auth endpoint, clear tokens and redirect to login
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;

      // Clear tokens and redirect to login
      clearTokens();

      // Dispatch a custom event that AuthContext can listen to
      window.dispatchEvent(new CustomEvent('auth:logout'));

      // Redirect to login
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export default api;
